import React, { useState, useEffect, useCallback } from "react";
import { X, Cookie, Shield, Settings, BarChart3, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "finx_cookie_consent";
const CATEGORIES = [
  { id: "necessary", icon: Shield, name: "Strictly Necessary", desc: "Required for site security, page navigation, form submission, and saving your privacy choices. These cookies cannot be turned off.", locked: true },
  { id: "functional", icon: Settings, name: "Functional", desc: "Help remember your preferences and improve your experience." },
  { id: "analytics", icon: BarChart3, name: "Analytics", desc: "Help us understand how visitors use FinX so we can improve performance and product experience." },
  { id: "marketing", icon: Target, name: "Marketing", desc: "Help measure marketing effectiveness and, where applicable, deliver more relevant content." },
];

const getInitialConsent = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (stored?.timestamp) return stored;
  } catch (e) { /* ignore parse errors */ }
  return null;
};

const persist = (prefs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    ...prefs,
    necessary: true,
    timestamp: new Date().toISOString(),
    version: 1,
  }));
  window.dispatchEvent(new CustomEvent("finx-cookie-consent-changed", { detail: prefs }));
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, functional: false, analytics: false, marketing: false });

  useEffect(() => {
    const stored = getInitialConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs((prev) => ({ ...prev, ...stored }));
    }
    const onReopen = () => {
      const s = getInitialConsent();
      if (s) setPrefs((prev) => ({ ...prev, ...s }));
      setPrefsOpen(true);
      setVisible(true);
    };
    const onDoNotSell = () => {
      const p = { necessary: true, functional: false, analytics: false, marketing: false };
      setPrefs(p);
      persist(p);
      setVisible(false);
      setPrefsOpen(false);
      try {
        const evt = new CustomEvent("finx-toast", {
          detail: { message: "Your preferences have been updated. Marketing and analytics cookies are now off." }
        });
        window.dispatchEvent(evt);
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("finx-open-cookie-preferences", onReopen);
    window.addEventListener("finx-do-not-sell", onDoNotSell);
    return () => {
      window.removeEventListener("finx-open-cookie-preferences", onReopen);
      window.removeEventListener("finx-do-not-sell", onDoNotSell);
    };
  }, []);

  const acceptAll = useCallback(() => {
    const p = { necessary: true, functional: true, analytics: true, marketing: true };
    setPrefs(p); persist(p); setVisible(false); setPrefsOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const p = { necessary: true, functional: false, analytics: false, marketing: false };
    setPrefs(p); persist(p); setVisible(false); setPrefsOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    persist(prefs); setVisible(false); setPrefsOpen(false);
  }, [prefs]);

  if (!visible) return null;

  return (
    <>
      {/* Bottom banner */}
      {!prefsOpen && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[100] rounded-3xl bg-white border border-slate-200 shadow-2xl p-6"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl brand-gradient flex items-center justify-center shrink-0">
              <Cookie size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-display font-black text-slate-900 text-base leading-tight">
                We use cookies
              </h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                We use cookies to operate FinX, improve performance, understand site usage, and, where applicable, support marketing. Read our{" "}
                <a href="/cookie-policy" className="text-[#5B21B6] font-semibold hover:underline">Cookie Policy</a>.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              data-testid="cookie-accept-all"
              onClick={acceptAll}
              className="w-full rounded-full h-11 font-bold text-white brand-gradient border-0 hover:opacity-95"
            >
              Accept All
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="cookie-reject"
                onClick={rejectNonEssential}
                variant="outline"
                className="rounded-full h-10 text-sm font-semibold border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Reject Non-Essential
              </Button>
              <Button
                data-testid="cookie-manage"
                onClick={() => setPrefsOpen(true)}
                variant="outline"
                className="rounded-full h-10 text-sm font-semibold border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Manage
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preference Center modal */}
      {prefsOpen && (
        <div
          data-testid="cookie-prefs-modal"
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setPrefsOpen(false)}
        >
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-6 md:p-8 border-b border-slate-100 flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-[#EDE9FE] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5B21B6] mb-3">
                  <Cookie size={12} /> Cookie Preferences
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-black text-slate-900 leading-tight">
                  Manage your cookie choices
                </h2>
                <p className="text-sm text-slate-600 mt-2 max-w-md">
                  Choose which cookie categories you allow. You can update this any time from the footer.
                </p>
              </div>
              <button
                data-testid="cookie-prefs-close"
                onClick={() => setPrefsOpen(false)}
                className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const checked = cat.locked ? true : prefs[cat.id];
                return (
                  <div
                    key={cat.id}
                    data-testid={`cookie-cat-${cat.id}`}
                    className="rounded-2xl border border-slate-100 p-5 flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#F5F3FF] text-[#5B21B6] flex items-center justify-center shrink-0">
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <h4 className="font-display font-bold text-slate-900">
                          {cat.name}
                          {cat.locked && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Always On</span>
                          )}
                        </h4>
                        <Switch
                          data-testid={`cookie-toggle-${cat.id}`}
                          checked={checked}
                          disabled={cat.locked}
                          onCheckedChange={(v) => !cat.locked && setPrefs((p) => ({ ...p, [cat.id]: v }))}
                        />
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{cat.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-6 md:p-8 border-t border-slate-100 flex flex-col sm:flex-row gap-3 bg-[#FAFAFA]">
              <Button
                data-testid="cookie-save-prefs"
                onClick={savePrefs}
                className="flex-1 rounded-full h-12 font-bold text-white brand-gradient border-0"
              >
                Save Preferences
              </Button>
              <Button
                data-testid="cookie-modal-reject"
                onClick={rejectNonEssential}
                variant="outline"
                className="rounded-full h-12 font-semibold border-slate-200 text-slate-700"
              >
                Reject Non-Essential
              </Button>
              <Button
                data-testid="cookie-modal-accept"
                onClick={acceptAll}
                variant="outline"
                className="rounded-full h-12 font-semibold border-slate-200 text-slate-700"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
